import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useFamilyStore } from '@/hooks/useStores';
import HelpDot from '@/components/help/HelpDot';
import { getInlineTipForContext } from '@/data/help-inline-tips';
import './claim-profile-modal.css';

interface ClaimProfileModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
  onClaimSuccess: (personId?: string) => void;
}

const ClaimProfileModal: React.FC<ClaimProfileModalProps> = observer(
  ({ familyId, isOpen, onClose, onClaimSuccess }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const familyStore = useFamilyStore();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isClaiming, setIsClaiming] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const claimProfileHelpTip = getInlineTipForContext('claim-profile-modal');

    // Load family data when modal opens
    useEffect(() => {
      if (isOpen && familyId) {
        setErrorMessage(null);
        // Ensure FamilyStore has the data loaded
        const loadData = async () => {
          try {
            // Only load if not already loaded or if it's a different family
            if (familyStore.familyId !== familyId || !familyStore.isLoaded) {
              await familyStore.loadFamilyData(familyId);
            }
            // Check if we have members after loading
            if (familyStore.people.size === 0) {
              setErrorMessage(t('claimProfile.errorNoMembers'));
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to load family data in ClaimProfileModal:', error);
            setErrorMessage(t('claimProfile.errorLoadFailed'));
          }
        };
        loadData();
      } else {
        // Reset state when modal closes
        setSearchQuery('');
        setErrorMessage(null);
      }
    }, [isOpen, familyId, familyStore, t]);

    // Get all family members from FamilyStore (excluding placeholders)
    const getAllFamilyMembers = () => {
      return Array.from(familyStore.people.values()).filter((person) => !person.isPlaceholder);
    };

    // Filter family members based on search query
    const getFilteredFamilyMembers = () => {
      const allMembers = getAllFamilyMembers();
      if (!searchQuery.trim()) {
        return allMembers;
      }

      const query = searchQuery.trim().toLowerCase();
      return allMembers.filter((person) => {
        const fullName = person.fullName.toLowerCase();
        return fullName.includes(query);
      });
    };

    const filteredFamilyMembers = getFilteredFamilyMembers();
    const isLoading = familyStore.isLoading || familyStore.familyId !== familyId;

    const handleClaim = async (familyMemberId: string) => {
      setIsClaiming(true);
      setErrorMessage(null);
      try {
        const result = await familyStore.claimProfile(familyMemberId);

        if (!result.success) {
          // Handle errors with specific messaging
          const error = result.error instanceof Error ? result.error : new Error(t('claimProfile.errorClaimFailed'));
          const claimErrorMessage = error.message || t('claimProfile.errorClaimFailed');

          // eslint-disable-next-line no-console
          console.error('Failed to claim profile:', claimErrorMessage);

          // Try to extract more specific error information if available
          if (claimErrorMessage.includes('auth') || claimErrorMessage.includes('sign in')) {
            setErrorMessage(t('claimProfile.errorSignIn'));
          } else if (claimErrorMessage.includes('already') || claimErrorMessage.includes('CONFLICT')) {
            setErrorMessage(t('claimProfile.errorAlreadyClaimed'));
          } else if (claimErrorMessage.includes('not found') || claimErrorMessage.includes('NOT_FOUND')) {
            setErrorMessage(t('claimProfile.errorNotFound'));
          } else if (claimErrorMessage.includes('network') || claimErrorMessage.includes('Network')) {
            setErrorMessage(t('claimProfile.errorNetwork'));
          } else {
            // API getUserMessage / server message passthrough
            setErrorMessage(claimErrorMessage);
          }
          return;
        }

        // Pass the personId from the response to update domainStore
        // This is the personId of the family member they claimed, not their original registration personId
        const personId = result.data?.personId || undefined;
        onClaimSuccess(personId);
      } catch (error) {
        // This catch handles unexpected errors
        // eslint-disable-next-line no-console
        console.error('Unexpected error claiming profile:', error);
        setErrorMessage(t('claimProfile.errorUnexpected'));
      } finally {
        setIsClaiming(false);
      }
    };

    const getPersonDisplayName = (person: {
      firstName: string;
      middleName: string | null;
      lastName: string;
    }): string => {
      const parts = [person.firstName, person.middleName, person.lastName].filter(Boolean);
      return parts.join(' ') || tCommon('unknown');
    };

    const getPersonBirthYear = (person: { birthDate: string | null }): string | null => {
      if (!person.birthDate) return null;
      try {
        const year = new Date(person.birthDate).getFullYear();
        return year.toString();
      } catch {
        return null;
      }
    };

    if (!isOpen) return null;

    return (
      <div className="v01-modal-overlay v01-modal-overlay--centered" onClick={onClose}>
        <div className="v01-modal claim-profile-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="claim-profile-modal-header">
            <h2 className="claim-profile-modal-title">{t('claimProfile.title')}</h2>
            <div className="claim-profile-modal-header-actions">
              {claimProfileHelpTip && (
                <HelpDot
                  title={claimProfileHelpTip.title}
                  body={claimProfileHelpTip.body}
                  storageKey={claimProfileHelpTip.storageKey}
                  docId="claiming-profile"
                />
              )}
              <button onClick={onClose} className="claim-profile-modal-close">
                ×
              </button>
            </div>
          </div>

          {/* Search Input */}
          <div className="claim-profile-modal-search">
            <input
              type="text"
              placeholder={t('person.searchByName')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="claim-profile-modal-search-input"
              autoFocus
            />
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="v01-modal__error" role="alert">
              <span>{errorMessage}</span>
              <button
                type="button"
                onClick={() => setErrorMessage(null)}
                className="v01-modal__error-dismiss"
                aria-label={t('actions.dismiss')}
              >
                ×
              </button>
            </div>
          )}

          {isLoading && (
            <div className="claim-profile-modal-loading">
              <div className="v01-modal__spinner" />
              <p className="claim-profile-modal-loading-text">{t('claimProfile.loading')}</p>
            </div>
          )}

          {/* Results */}
          {!isLoading && (
            <>
              {filteredFamilyMembers.length === 0 ? (
                <div className="claim-profile-modal-empty">
                  {searchQuery.trim() ? <p>{t('claimProfile.emptyNoMatch')}</p> : <p>{t('claimProfile.emptyNone')}</p>}
                </div>
              ) : (
                <div className="claim-profile-modal-results">
                  {filteredFamilyMembers.map((person) => {
                    const displayName = getPersonDisplayName(person);
                    const birthYear = getPersonBirthYear(person);
                    return (
                      <div key={person.familyMemberId} className="claim-profile-modal-person">
                        <div className="claim-profile-modal-person-info">
                          <div className="claim-profile-modal-person-name">{displayName}</div>
                          {birthYear && (
                            <div className="claim-profile-modal-person-birth">
                              {t('claimProfile.bornYear', { year: birthYear })}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleClaim(person.familyMemberId)}
                          disabled={isClaiming}
                          className="btn btn--primary btn--sm"
                        >
                          {isClaiming ? t('actions.claiming') : t('actions.claimProfile')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Footer */}
          <div className="claim-profile-modal-footer">
            <button type="button" onClick={onClose} className="btn btn--ghost">
              {tCommon('cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default ClaimProfileModal;
